import * as React from 'react';

import { Input, InputProps } from '@lightspeed/flame/Input';
import { UseMutationResult } from '@tanstack/react-query';
import axios from 'axios';

import { SENSITIVE_INFO_WARNING } from 'root/merchantos/utils';
import { hasCreditCardInfo } from 'root/regexValidation';

export interface RowEditInputNumberProps extends InputProps {
  value: number;
  fieldName: string;
  mutationUpdate: UseMutationResult<any, unknown, any>;
  panDetection?: boolean;
  autoSelect?: boolean;
  roundingMethod?: (value: number) => number;
  min?: number;
  max?: number;
}

export const RowEditInputNumber: React.FC<RowEditInputNumberProps> = ({
  value,
  fieldName,
  mutationUpdate,
  panDetection = false,
  autoSelect = true,
  roundingMethod = (value) => value,
  min,
  max,
  ...rest
}): React.ReactElement => {
  const [inputValue, setInputValue] = React.useState(value.toString());
  const [parsingError, setParsingError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  React.useEffect(() => {
    if (panDetection && hasCreditCardInfo(inputValue)) {
      setParsingError(SENSITIVE_INFO_WARNING);
      return;
    }
    setParsingError(undefined);
  }, [inputValue, mutationUpdate, panDetection]);

  const saveValue = (): void => {
    const parsedValue = roundingMethod(Number(inputValue.trim()));
    if (isNaN(parsedValue)) {
      throw new Error('Invalid number format');
    }
    if (parsedValue === value) {
      return;
    }
    if (min != null && parsedValue < min) {
      throw new Error(`Value cannot be less than ${min}`);
    }
    if (max != null && parsedValue > max) {
      throw new Error(`Value cannot be greater than ${max}`);
    }
    mutationUpdate.mutate({
      [fieldName]: parsedValue,
    });
    setInputValue(parsedValue.toString());
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      try {
        saveValue();
      } catch (error) {
        setParsingError(error.message);
      }
    }
  };
  const handleBlur = (_event: React.FocusEvent<HTMLInputElement>) => {
    try {
      saveValue();
    } catch (error) {
      setParsingError(error.message);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (mutationUpdate.isError) mutationUpdate.reset();
    setInputValue(event.target.value);
  };
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (autoSelect) {
      event.currentTarget.select();
    }
  };

  React.useEffect(() => {
    if (mutationUpdate.isLoading) {
      window.showNotifier('working');
    } else if (mutationUpdate.isSuccess) {
      window.hideNotifier('working');
      window.flashNotifier('success', 'Saved Changes', 3);
    } else if (mutationUpdate.isError) {
      window.hideNotifier('working');
    }
    // only update the notifier if the loading state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutationUpdate.isLoading]);

  const errorMessage = React.useMemo(() => {
    if (mutationUpdate.isError) {
      if (axios.isAxiosError(mutationUpdate.error)) {
        if (mutationUpdate.error.response?.data?.message)
          return mutationUpdate.error.response.data.message;
        if (mutationUpdate.error.response?.data?.errors?.[0]?.detail)
          return mutationUpdate.error.response.data.errors[0].detail;
      }
      return 'Something went wrong. Try again.';
    }
    return undefined;
  }, [mutationUpdate.error, mutationUpdate.isError]);

  return (
    <Input
      autoComplete="off"
      value={inputValue}
      onFocus={handleFocus}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      onBlur={handleBlur}
      disabled={mutationUpdate.isLoading}
      status={mutationUpdate.isError || parsingError ? 'error' : undefined}
      statusMessage={errorMessage || parsingError}
      type="number"
      {...rest}
    />
  );
};
